import type { JSX } from 'react'

import { Icon } from '../design-system/icon.tsx'
import { cn } from '../lib/cn.ts'
import { timeZoneOptions } from '../lib/time-zone.ts'
import { useTimeZone } from './time-zone-provider.tsx'

const OPTIONS = timeZoneOptions()

export interface TimeZoneSelectProps {
  'data-testid': string
  className?: string
}

/**
 * TimeZoneSelect — the reader's zone, held in this browser like the theme.
 *
 * A rail row, not the page `Select`. That control is a light form field, and
 * a light field in the navy foot is a second surface. The closed control
 * reads like Sign out. The menu is the browser's.
 *
 * @param props - Select props.
 * @param props.data-testid - Test hook.
 * @param props.className - Extra classes for the row.
 * @returns The rendered select.
 */
export function TimeZoneSelect({
  'data-testid': testId,
  className
}: TimeZoneSelectProps): JSX.Element {
  const { timeZone, setTimeZone } = useTimeZone()
  const options = OPTIONS.some(option => option.value === timeZone)
    ? OPTIONS
    : [{ value: timeZone, label: timeZone }, ...OPTIONS]

  return (
    <label
      className={cn(
        'text-sidebar-foreground/80 relative flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm',
        'hover:bg-sidebar-accent/40 focus-within:ring-sidebar-ring focus-within:ring-2 focus-within:outline-none',
        className
      )}
    >
      <Icon name="clock" className="size-4 shrink-0" />
      <select
        data-testid={testId}
        aria-label="Time zone"
        value={timeZone}
        className="w-full min-w-0 cursor-pointer appearance-none bg-transparent pr-6 outline-none"
        onChange={event => setTimeZone(event.target.value)}
      >
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <Icon
        name="chevron-down"
        className="pointer-events-none absolute right-3 size-4 shrink-0"
      />
    </label>
  )
}
